import { useContext, useMemo, useState } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import moment from "moment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  API,
  EInvoiceStatusCode,
  INTERNAL_REJECTION_VENDOR_LABEL,
  INVOICE_STATUS_TILE_ORDER,
  UserRole,
  getInvoiceStatusLabel,
  isInternalRejectionReason,
  isSuperAdminRole,
  isSupersededInvoice,
} from "../../../constants";
import { useGetService, useUserRole } from "../../../hooks";
import { GlobalContextData } from "../../../context";
import { useLocation, useNavigate } from "react-router-dom";
import { MGButton, SelectDropdown } from "../../../components";
import {
  IInvoiceListItem,
  buildInvoiceListUrl,
  formatPeriodDay,
  isHotelInvoice,
  mapInvoiceListItem,
  scopeInvoicesForUser,
  selectInvoiceList,
  useInternalRejectionReasonCodes,
  useInvoiceListQuery,
} from "../../../api/invoices";
import { formatMoney } from "../InvoiceDetails/invoiceBreakdown";

type InvoiceBucket = EInvoiceStatusCode;

const getInvoiceBucket = (invoice: IInvoiceListItem): InvoiceBucket | null => {
  const status = invoice?.status as EInvoiceStatusCode;
  return INVOICE_STATUS_TILE_ORDER.includes(status) ? status : null;
};

const BUCKET_CHIP_COLOR: Record<InvoiceBucket, "default" | "error" | "success"> = {
  [EInvoiceStatusCode.NEW]: "default",
  [EInvoiceStatusCode.APPROVED]: "success",
  [EInvoiceStatusCode.SUBMITTED]: "success",
  [EInvoiceStatusCode.REJECTED]: "default",
  [EInvoiceStatusCode.PAID]: "success",
  [EInvoiceStatusCode.SUPERSEDED]: "default",
};


type NoRowsOverlayProps = { message: string; hint?: string };

declare module "@mui/x-data-grid" {
  interface NoRowsOverlayPropsOverrides extends NoRowsOverlayProps {}
}

const InvoicesNoRowsOverlay = ({ message, hint }: NoRowsOverlayProps) => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      px: 2,
      textAlign: "center",
    }}
  >
    <Typography variant="body1" fontWeight={500}>
      {message}
    </Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

const RANGE_PRESETS: { key: string; label: string; days: number }[] = [
  { key: "15", label: "15 Days", days: 15 },
  { key: "30", label: "30 Days", days: 30 },
  { key: "60", label: "60 Days", days: 60 },
  { key: "90", label: "90 Days", days: 90 },
  { key: "180", label: "180 Days", days: 180 },
];
const DEFAULT_RANGE_KEY = "15";
const MAX_RANGE_KEY = String(API.INVOICE.LIST_WINDOW_DAYS_MAX);

const resolveRange = (rangeKey: string): { startDate: string; endDate: string } => {
  const preset =
    RANGE_PRESETS.find((p) => p.key === rangeKey) ??
    RANGE_PRESETS.find((p) => p.key === DEFAULT_RANGE_KEY)!;
  return API.INVOICE.resolveListWindow(preset.days);
};

const escapeCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const exportInvoicesToCsv = (
  rows: any[],
  range: { startDate: string; endDate: string },
  role: UserRole | null
) => {
  const headers = [
    "Invoice #",
    "Total Amount Due",
    "Invoice Status",
    "Payment Status",
    "Updated On",
  ];
  const lines = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) =>
      [
        row.invoiceNumber ?? "",
        typeof row.amountDue === "number" && Number.isFinite(row.amountDue)
          ? row.amountDue.toFixed(2)
          : "",
        getInvoiceStatusLabel(row.status ?? "", role),
        row.paymentStatus ?? "",
        (row.updatedOn ?? row.createdOn)
          ? moment(row.updatedOn ?? row.createdOn).format("MM/DD/YYYY")
          : "",
      ]
        .map(escapeCsvValue)
        .join(",")
    ),
  ];
  const csv = String.fromCharCode(0xfeff) + `${lines.join("\r\n")}\r\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoices-${range.startDate}_to_${range.endDate}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const InvoiceList = () => {
  const navigate = useNavigate();
  const { state } = useContext(GlobalContextData);

  const role = useUserRole();
  const isSuperAdmin = isSuperAdminRole(role);
  const isRoleLoading = role === null;

  const [rangeKey, setRangeKey] = useState<string>(DEFAULT_RANGE_KEY);

  const range = useMemo(() => resolveRange(rangeKey), [rangeKey]);

  const listUrl = useMemo(
    () =>
      API.INVOICE.USE_BEDROCK_INVOICES
        ? buildInvoiceListUrl(range, state.userInfo)
        : API.INVOICE.LIST,
    [range, state.userInfo]
  );
  const {
    response,
    isLoading: isFetching,
    error,
    detailedError,
  } = useGetService(listUrl);

  const isLoading = isFetching || isRoleLoading;

  const { data: internalReasonCodes } = useInternalRejectionReasonCodes(
    !isRoleLoading && !isSuperAdmin
  );

  const [activeBucket, setActiveBucket] = useState<InvoiceBucket | null>(null);

  const location = useLocation();
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState<string | null>(
    () => (location.state as { invoiceNumber?: string } | null)?.invoiceNumber ?? null
  );

  const [selectedVendor, setSelectedVendor] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const resetPage = () => setPaginationModel((model) => ({ ...model, page: 0 }));

  const loadFailed = !isFetching && !!error;

  const loadFailedMessage: string | undefined =
    (error as any)?.response?.data?.error ??
    (typeof error === "string" ? error : (error as any)?.message);

  const allInvoices: IInvoiceListItem[] = useMemo(
    () =>
      loadFailed || isRoleLoading
        ? []
        : scopeInvoicesForUser(
            selectInvoiceList(response)
              .map(mapInvoiceListItem)
              .filter(
                (inv) => isHotelInvoice(inv) && !isSupersededInvoice(inv.status)
              ),
            state.userInfo
          ).map((inv) =>
            !isSuperAdmin &&
            isInternalRejectionReason(inv.rejectionReasonCd, internalReasonCodes)
              ? {
                  ...inv,
                  vendorRejectionReason: INTERNAL_REJECTION_VENDOR_LABEL,
                  rejectionComments: undefined,
                }
              : inv
          ),
    [
      response,
      state.userInfo,
      loadFailed,
      isRoleLoading,
      isSuperAdmin,
      internalReasonCodes,
    ]
  );

  const dateFilteredInvoices = allInvoices;

  const vendorOptions = useMemo(() => {
    const byVendor = new Map<string, string>();
    allInvoices.forEach((inv) => {
      if (inv?.vendorId != null) {
        byVendor.set(
          String(inv.vendorId),
          inv.vendorName ?? String(inv.vendorId)
        );
      }
    });
    return [
      { label: "All Vendors", value: "" },
      ...Array.from(byVendor, ([value, label]) => ({ value, label })),
    ];
  }, [allInvoices]);

  const vendorFilteredInvoices = useMemo(() => {
    if (!selectedVendor || !selectedVendor.value) return dateFilteredInvoices;
    return dateFilteredInvoices.filter(
      (inv) => String(inv.vendorId) === selectedVendor.value
    );
  }, [dateFilteredInvoices, selectedVendor]);

  const STATUS_TILES = useMemo(
    () =>
      INVOICE_STATUS_TILE_ORDER.map((code) => ({
        key: code,
        label: getInvoiceStatusLabel(code, role),
      })),
    [role]
  );

  const counts = useMemo(() => {
    const c = INVOICE_STATUS_TILE_ORDER.reduce(
      (acc, code) => ({ ...acc, [code]: 0 }),
      {} as Record<InvoiceBucket, number>
    );
    vendorFilteredInvoices.forEach((inv) => {
      const bucket = getInvoiceBucket(inv);
      if (bucket) c[bucket] += 1;
    });
    return c;
  }, [vendorFilteredInvoices]);

  const tableRows = useMemo(() => {
    const byNumber = invoiceNumberFilter
      ? vendorFilteredInvoices.filter(
          (inv) => String(inv.invoiceNumber ?? "") === invoiceNumberFilter
        )
      : vendorFilteredInvoices;
    if (!activeBucket) return byNumber;
    return byNumber.filter((inv) => getInvoiceBucket(inv) === activeBucket);
  }, [vendorFilteredInvoices, activeBucket, invoiceNumberFilter]);

  const tiles = [
    {
      key: "all" as const,
      label: "All Invoices",
      value: vendorFilteredInvoices.length,
    },
    ...STATUS_TILES.map((t) => ({
      key: t.key,
      label: t.label,
      value: counts[t.key],
    })),
  ];

  const handleView = (params: any) => {
    navigate(`/invoicing/${params.id}`, {
      state: {
        invoice: params.row
      }
    });
  };

  const rangeLabel = `${moment(range.startDate).format("MM/DD/YYYY")} – ${moment(
    range.endDate
  ).format("MM/DD/YYYY")}`;

  const hasActiveFilters =
    !!activeBucket ||
    !!selectedVendor?.value ||
    rangeKey !== DEFAULT_RANGE_KEY ||
    !!invoiceNumberFilter;

  const resetFilters = () => {
    setActiveBucket(null);
    setInvoiceNumberFilter(null);
    setSelectedVendor(null);
    setRangeKey(DEFAULT_RANGE_KEY);
    resetPage();
  };

  const emptyState: NoRowsOverlayProps = (() => {
    if (loadFailed) {
      return { message: "Invoices could not be loaded." };
    }
    const bucketLabel = STATUS_TILES.find((t) => t.key === activeBucket)?.label;
    const vendorLabel = selectedVendor?.value ? selectedVendor.label : undefined;
    if (bucketLabel || vendorLabel) {
      return {
        message: `No ${bucketLabel ? `${bucketLabel.toLowerCase()} ` : ""}invoices${
          vendorLabel ? ` for ${vendorLabel}` : ""
        } updated ${rangeLabel}.`,
        hint: "Clear a filter or widen the date range to see more.",
      };
    }
    return {
      message: `No invoices are available.`,
      hint:
        rangeKey === MAX_RANGE_KEY
          ? "180 days is the widest range available."
          : "Try a wider date range.",
    };
  })();

  const columnHeaderData = useMemo(() => {
    return [
      {
        field: "invoiceNumber",
        headerName: "Invoice #",
        flex: 1,
      },
      ...(isSuperAdmin
        ? [
            {
              field: "vendorName",
              headerName: "Vendor",
              flex: 1.2,
              valueGetter: (_value: any, row: any) =>
                row?.vendorName ?? row?.vendorId ?? "",
              renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ lineHeight: 1.3 }}>
                  <Typography variant="body2">{params.value}</Typography>
                  {params.row?.vendorCode && (
                    <Typography variant="caption" color="text.secondary">
                      Code {params.row.vendorCode}
                    </Typography>
                  )}
                </Box>
              ),
            },
          ]
        : []),
      {
        field: "invoicePeriod",
        headerName: "Invoice Period",
        flex: 1.4,
        type: "date",
        valueGetter: (_value: any, row: any) =>
          row?.periodStart ? new Date(row.periodStart) : null,
        renderCell: (params: GridRenderCellParams) => {
          const start = params.row?.periodStart;
          const end = params.row?.periodEnd;
          if (!start && !end) return "—";
          return [formatPeriodDay(start), formatPeriodDay(end)]
            .filter(Boolean)
            .join(" – ");
        },
      },
      {
        field: "amountDue",
        headerName: "Total Amount Due",
        flex: 1,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value?: number) =>
          typeof value === "number" && Number.isFinite(value)
            ? formatMoney(value)
            : "",
      },
      {
        field: "status",
        headerName: "Invoice Status",
        flex: 1.1,
        renderCell: (params: GridRenderCellParams) => {
          const bucket = getInvoiceBucket(params.row);
          return (
            <Chip
              label={getInvoiceStatusLabel(params.value ?? "", role)}
              color={bucket ? BUCKET_CHIP_COLOR[bucket] : "default"}
            />
          );
        },
      },
      {
        field: "vendorRejectionReason",
        headerName: "Review Notes",
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          if (!params.value) return "—";
          const comments = params.row?.rejectionComments;
          const text = (
            <span
              style={{
                whiteSpace: "normal",
                lineHeight: 1.3,
                textDecoration: comments ? "underline dotted" : undefined,
                cursor: comments ? "help" : undefined,
              }}
            >
              {params.value}
            </span>
          );
          return comments ? (
            <Tooltip title={comments} arrow placement="top">
              {text}
            </Tooltip>
          ) : (
            text
          );
        },
      },
      {
        field: "updatedOn",
        headerName: "Updated On",
        flex: 1,
        type: "date",
        valueGetter: (_value: any, row: any) => {
          const updated = row.updatedOn ?? row.createdOn;
          return updated ? new Date(updated) : null;
        },
        valueFormatter: (value?: Date) =>
          value ? moment(value).format("MM/DD/YYYY") : "",
      },
      {
        field: "actions",
        type: "actions",
        width: 80,
        getActions: (params: any) => [
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="View"
            onClick={() => handleView(params)}
          />,
        ],
      },
    ] as GridColDef[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isSuperAdmin]);

  return (
    <section className="invoicing-details-container">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "grey.200",
          borderRadius: 1,
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ flex: 1, textAlign: "left" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            INVOICES
          </Typography>
          <Typography variant="body2" color="text.secondary">
            From {rangeLabel}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
          <Tooltip
            title={
              tableRows.length === 0
                ? "Nothing to export for the current filters"
                : `Download the ${tableRows.length} invoice${
                    tableRows.length === 1 ? "" : "s"
                  } shown as CSV`
            }
            arrow
          >
            <span>
              <MGButton
                onClick={() => exportInvoicesToCsv(tableRows, range, role)}
                disabled={tableRows.length === 0}
              >
                Export CSV
              </MGButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          marginTop: 2,
          marginBottom: 1,
        }}
      >
        {tiles.map((item) => {
          const isActive =
            item.key === "all" ? !activeBucket : activeBucket === item.key;
          return (
            <Card
              key={item.key}
              variant="outlined"
              sx={{
                flex: "1 1 160px",
                minWidth: 150,
                borderRadius: 2,
                borderWidth: isActive ? 2 : 1,
                borderColor: isActive ? "primary.main" : undefined,
                backgroundColor: isActive ? "action.selected" : undefined,
                transition: "border-color 120ms, box-shadow 120ms",
                "&:hover": { borderColor: "primary.main", boxShadow: 1 },
              }}
            >
              <CardActionArea
                onClick={() => {
                  setActiveBucket(
                    item.key === "all" ? null : (item.key as InvoiceBucket)
                  );
                  resetPage();
                }}
                role="button"
                aria-pressed={isActive}
                aria-label={`Show ${item.label.toLowerCase()} (${item.value})`}
              >
                <CardContent sx={{ textAlign: "center", position: "relative" }}>
                  {isActive && (
                    <CheckCircleIcon
                      color="primary"
                      fontSize="small"
                      sx={{ position: "absolute", top: 8, right: 8 }}
                    />
                  )}
                  <Typography variant="h4" component="div" fontWeight={600}>
                    {isLoading ? (
                      <Skeleton width={48} sx={{ mx: "auto" }} />
                    ) : (
                      item.value
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          marginTop: 3,
          marginBottom: 1,
          "& .select-box-container": { marginTop: 0, minWidth: 220 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            Updated within:
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={rangeKey}
            onChange={(_, next: string | null) => {
              if (!next) return;
              setRangeKey(next);
              resetPage();
            }}
            aria-label="Filter invoices by last updated"
            sx={{ "& .MuiToggleButton-root": { px: 2, textTransform: "none" } }}
          >
            {RANGE_PRESETS.map((preset) => (
              <ToggleButton key={preset.key} value={preset.key}>
                {preset.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {isSuperAdmin && (
          <SelectDropdown
            containerClassName="date-box"
            label=""
            placeholder="All vendors"
            aria-label="Filter invoices by vendor"
            options={vendorOptions}
            value={selectedVendor}
            onChange={(data: any) => {
              setSelectedVendor(data);
              resetPage();
            }}
            isClearable
            styles={{
              control: (base: any) => ({ ...base, minHeight: 38 }),
            }}
          />
        )}

        {invoiceNumberFilter && (
          <Chip
            label={`Invoice ${invoiceNumberFilter}`}
            onDelete={() => {
              setInvoiceNumberFilter(null);
              resetPage();
            }}
            color="primary"
            variant="outlined"
            aria-label={`Showing only invoice ${invoiceNumberFilter}. Remove filter.`}
          />
        )}

        <Box
          sx={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" noWrap>
            {isLoading
              ? "Loading…"
              : `${tableRows.length} invoice${tableRows.length === 1 ? "" : "s"}`}
          </Typography>
          {hasActiveFilters && (
            <Button size="small" onClick={resetFilters} sx={{ textTransform: "none" }}>
              Reset filters
            </Button>
          )}
        </Box>
      </Box>

      {loadFailed && (
        <Box
          sx={{
            marginTop: 2,
            px: 3,
            py: 2,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "warning.main",
            backgroundColor: "warning.light",
          }}
        >
          <Typography variant="body2">
            We couldn't load your invoices right now
            {loadFailedMessage ? ` (${loadFailedMessage})` : ""}. The table below
            is empty because of that — not because there are no invoices. Please
            try again, and contact CCL support if it keeps happening.
          </Typography>
        </Box>
      )}

      <DataGrid
        rows={tableRows}
        getRowHeight={() => "auto"}
        sx={{
          height: tableRows.length === 0 ? 220 : "auto",
          marginTop: 2,
          "& .MuiDataGrid-cell": { display: 'flex', alignItems: "center", py: 1.3 },
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
        }}
        columns={columnHeaderData || []}
        loading={isLoading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25]}
        initialState={{
          sorting: { sortModel: [{ field: "updatedOn", sort: "desc" }] },
        }}
        onRowClick={(params) => handleView(params)}
        disableRowSelectionOnClick
        slots={{ noRowsOverlay: InvoicesNoRowsOverlay }}
        slotProps={{ noRowsOverlay: emptyState }}
      />
    </section>
  );
};
